import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, Button, hasConsented } from "@city-sage/aurum-avis-labs-components";
import { motion } from "framer-motion";
import { Compass, Plus, Backpack } from "lucide-react";
import { Link } from '@tanstack/react-router';

// Welcome Modal Component
interface WelcomeModalProps {
  onExploreStories: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ 
  onExploreStories
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Cookie utilities for visit tracking
  const setCookie = useCallback((name: string, value: string, days: number) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }, []);

  const getCookie = useCallback((name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }, []);

  // Check for first visit and show welcome modal
  useEffect(() => {
    const checkAndShowModal = () => {
      const hasVisited = getCookie('citysage-has-visited');
      // Use new consent system
      const hasCookieConsent = hasConsented();
      
      // Only show welcome modal if user hasn't visited AND has handled cookie consent
      if (!hasVisited && hasCookieConsent && !isOpen) {
        setIsOpen(true);
      }
    };

    // Check immediately
    checkAndShowModal();
    
    // Listen for consent updates
    const handleConsentUpdate = () => {
      checkAndShowModal();
    };
    
    window.addEventListener('consentUpdated', handleConsentUpdate);
    
    // Poll every 1 second to check for consent changes (fallback)
    const interval = setInterval(checkAndShowModal, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('consentUpdated', handleConsentUpdate);
    };
  }, [getCookie, isOpen]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Set cookie to expire in 1 year
    setCookie('citysage-has-visited', 'true', 365);
  }, [setCookie]);

  // Handle explore stories action
  const handleExploreStories = useCallback(() => {
    handleClose();
    onExploreStories();
  }, [handleClose, onExploreStories]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-w-[90vw] p-0 overflow-hidden border-0 z-[50]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative"
        >
          {/* Header with brand gradient background */}
          <div className="relative px-6 pt-8 pb-6 text-primary overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-white/10 rounded-full blur-lg"></div>
            
            <div className="relative z-10 text-center">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Backpack className="h-12 w-12 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2 text-black">Welcome, Traveler!</h2>
                <p className="text-muted-foreground text-lg">
                  Turn your journeys into captivating stories
                </p>
              </motion.div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-center space-y-4"
            >
              <h3 className="text-xl font-semibold text-foreground">
                Discover CitySage
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Create beautiful travel itineraries, explore inspiring stories from fellow travelers, 
                and turn your adventures into shareable visual experiences.
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="space-y-3"
            >

              <Button
                onClick={handleExploreStories}
                className="w-full"
                size="lg"
              >
                <Compass className="" />
                Explore Itineraries
              </Button>
              <Link className="w-full inline-block" to="/itineraries/create" onClick={handleClose}>
              <Button
                variant="outline"
                className="w-full"
                size="lg"
              >
                <Plus className="" />
                Create Your Adventure
              </Button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}; 